import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { FileInput, type FileInputProps } from '../primitives/FileInput';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

export interface RhfFileInputProps
  extends Omit<FileInputProps, 'name' | 'value' | 'onChange' | 'error'>, RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
}

export const RhfFileInput = React.forwardRef<HTMLInputElement, RhfFileInputProps>(
  function RhfFileInput(
    {
      control,
      name,
      label,
      hint,
      tooltip,
      required = false,
      semanticId,
      disabled = false,
      onFileSelect,
      controlWidth,
      ...rest
    },
    ref,
  ) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          return (
            <FormField
              label={label}
              hint={hint}
              tooltip={tooltip}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <FileInput
                ref={composeRefs(field.ref, ref)}
                name={field.name}
                error={Boolean(errorText)}
                disabled={disabled}
                onFileSelect={(files) => {
                  field.onChange(files);
                  onFileSelect?.(files);
                }}
                onBlur={field.onBlur}
                semanticId={semanticId}
                {...rest}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfFileInput.displayName = 'RhfFileInput';
